import { Link } from "react-router-dom";
import { ChevronRight, Sparkles, ArrowRight, Truck, MapPinned, RefreshCw, Users, Check } from "lucide-react";
import Reveal from "../components/Reveal";
import { ASSETS } from "../lib/brand";
import CTAStrip from "../components/CTAStrip";

const ACCENT = "#059669";

const SECTIONS = [
  { icon: Truck, title: "Warehouse → Vehicle Stock Transfer", items: ["Vehicle stock allocation", "Route-wise stock management", "Check-in and Check-out for sales staff"] },
  { icon: MapPinned, title: "Mobile Van Sales", items: ["On-the-go billing", "Customer-wise pricing", "Collection management", "Credit sales handling"] },
  { icon: RefreshCw, title: "Real-Time Synchronization", items: ["Auto sync with DigiPOS Zen", "Stock updates in real time", "Collection updates", "Inventory reconciliation"] },
  { icon: Users, title: "Distribution Management", items: ["Route management", "Salesman performance tracking", "Customer visit management", "Outstanding collection tracking"] },
];

const BENEFITS = ["Faster order processing", "Reduced manual work", "Improved stock accuracy", "Complete field sales visibility"];
const IDEAL = ["FMCG Distributors", "Wholesale Businesses", "Beverage Distributors", "Food Product Distributors", "Field Sales Teams"];

export default function DigiposOngo() {
  return (
    <div data-testid="digipos-ongo-page">
      <section className="relative pt-32 pb-24 overflow-hidden" style={{ background: `linear-gradient(135deg, #0B2545 0%, #064E3B 50%, ${ACCENT} 100%)` }}>
        <div className="absolute inset-0 gi-mesh opacity-40" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center text-white">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="flex items-center gap-2 text-white/60 text-sm mb-4">
                <Link to="/" className="hover:text-white">Home</Link><ChevronRight className="h-3 w-3" />
                <Link to="/digipos" className="hover:text-white">DigiPOS</Link><ChevronRight className="h-3 w-3" />
                <span className="text-white">Ongo</span>
              </div>
              <span className="gi-pill-dark"><Sparkles className="h-3.5 w-3.5" /> For Distributors & Field Sales</span>
              <h1 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl mt-4 leading-tight">DigiPOS Ongo</h1>
              <p className="font-display text-xl mt-2" style={{ color: "#A7F3D0" }}>Smart Van Sales & Distribution</p>
              <p className="text-white/75 mt-5 max-w-xl">
                A dedicated Van Sales application integrated with DigiPOS Zen. Transfer stock from warehouses to vehicles, bill at customer locations and sync everything back to your central ERP automatically.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/contact" data-testid="ongo-demo-btn" className="gi-btn-primary">Request Demo <ArrowRight className="h-4 w-4" /></Link>
                <Link to="/digipos/zen" className="gi-btn-ghost">View Zen Integration</Link>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={120}>
              <div className="relative gi-float">
                <div className="absolute -inset-6 bg-gradient-to-br from-[#10B981]/40 to-transparent blur-3xl rounded-full" />
                <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-white border border-white/10">
                  <img src={ASSETS.ongo} alt="DigiPOS Ongo" className="w-full h-auto block" />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-white gi-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <span className="font-mono text-xs uppercase tracking-widest" style={{ color: ACCENT }}>How It Works</span>
            <h2 className="font-display font-bold text-3xl md:text-4xl mt-2">From warehouse to last mile, on one app</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            {SECTIONS.map((s, i) => (
              <Reveal key={s.title} delay={i * 70}>
                <div className="gi-card gi-card-hover p-7" style={{ borderTop: `4px solid ${ACCENT}` }}>
                  <div className="h-12 w-12 rounded-xl grid place-items-center text-white mb-4" style={{ background: ACCENT }}>
                    <s.icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-display font-semibold text-lg mb-3">{s.title}</h3>
                  <ul className="space-y-1.5 text-sm text-[#1A1A2E]/85">
                    {s.items.map((it) => <li key={it} className="flex items-start gap-2"><span className="h-1.5 w-1.5 rounded-full mt-2 shrink-0" style={{ background: ACCENT }} />{it}</li>)}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F5F8FF] gi-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
          <Reveal>
            <div className="gi-card p-8 h-full" style={{ borderTop: `4px solid ${ACCENT}` }}>
              <h3 className="font-display font-bold text-2xl mb-6">Benefits</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {BENEFITS.map((b) => (
                  <div key={b} className="flex items-start gap-3 p-4 rounded-xl bg-[#F5F8FF] border border-[#DDE6F0]">
                    <span className="h-7 w-7 rounded-md grid place-items-center shrink-0 text-white" style={{ background: ACCENT }}>
                      <Check className="h-4 w-4" />
                    </span>
                    <span className="text-sm font-medium leading-snug">{b}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div className="gi-card p-8 h-full" style={{ borderTop: `4px solid ${ACCENT}` }}>
              <h3 className="font-display font-bold text-2xl mb-6">Ideal For</h3>
              <div className="flex flex-wrap gap-2">
                {IDEAL.map((i) => (
                  <span key={i} className="px-4 py-2 rounded-full bg-[#F5F8FF] border border-[#DDE6F0] hover:border-[#059669] hover:text-[#059669] hover:bg-white transition text-sm font-medium">{i}</span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <CTAStrip heading="Take field sales digital with Ongo" sub="Pair Ongo with Zen for a complete distribution-grade ERP setup." />
    </div>
  );
}
